import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, type ReactNode } from 'react';
import { EntryReportSummary } from '../../garage/components/reception/EntryReportSummary.js';
import { downloadRoute } from '../../garage/lib/download.js';
import { formatDateTime } from '../../garage/lib/format.js';
import { ApiError, api } from '../../shared/api.js';
import { Button } from '../../ui/Button.js';
import { PORTAL_QUERY_KEY } from '../keys.js';
import { usePortalPreview } from '../preview.js';
import { useTz } from '../timezone.js';
import { getPortalToken } from '../token.js';
import { uuid } from '../uuid.js';

export const PORTAL_ENTRY_REPORT_KEY = [...PORTAL_QUERY_KEY, 'entry-report'] as const;

/**
 * Fiche de réception côté client (vague 6, ajout 10) : dernière version validée et partagée, en lecture ;
 * téléchargement du PDF (jeton en en-tête, jamais dans l'adresse) ; « J'ai pris connaissance de l'état
 * constaté » liée à la version exacte, idempotente, horodatée par le serveur. Aucune fiche : rien n'est affiché.
 * Compte rendu (`readOnly`) : consultation seulement.
 */
export function PortalEntryReport({ readOnly = false, signed = false, empty = null }: { readOnly?: boolean; signed?: boolean; empty?: ReactNode }) {
  const qc = useQueryClient();
  const tz = useTz();
  const keyRef = useRef(new Map<number, string>());
  // Aperçu côté garage (vague 7) : la fiche n'est pas demandée au portail (aucun jeton).
  const preview = usePortalPreview() !== null;
  const report = useQuery({
    queryKey: PORTAL_ENTRY_REPORT_KEY,
    queryFn: () => api('portal.entryReport'),
    enabled: !preview,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });
  const ack = useMutation({
    mutationFn: (version: number) => {
      let idempotencyKey = keyRef.current.get(version);
      if (!idempotencyKey) {
        idempotencyKey = uuid();
        keyRef.current.set(version, idempotencyKey);
      }
      return api('portal.acknowledgeEntryReport', { body: { version, idempotencyKey } });
    },
    onSuccess: (r) => qc.setQueryData(PORTAL_ENTRY_REPORT_KEY, r),
    onError: async (err) => {
      if (err instanceof ApiError && err.status === 409) await qc.invalidateQueries({ queryKey: PORTAL_ENTRY_REPORT_KEY });
    },
  });
  const pdf = useMutation({
    mutationFn: () => downloadRoute('portal.entryReportPdf', undefined, { fallbackName: 'fiche-de-reception.pdf', portalToken: getPortalToken() }),
  });

  const r = report.data;
  if (report.isPending && !preview) {
    return (
      <p role="status" className="px-4 pt-8 text-ink-3 sm:px-0">
        Chargement de la fiche…
      </p>
    );
  }
  if (!r) {
    // 404 : aucune fiche partagée ; autre erreur : message neutre (le reste du suivi reste lisible).
    if (report.error instanceof ApiError && report.error.status !== 404) {
      return (
        <p role="alert" className="mx-4 mt-8 rounded-xl border border-danger-line bg-danger-bg p-3.5 text-danger-fg sm:mx-0">
          La fiche de réception n’a pas pu être chargée. Vérifiez votre connexion et réessayez.
        </p>
      );
    }
    return <>{empty}</>;
  }
  return (
    <section id="fiche-reception" className="scroll-mt-4 px-4 pt-8 sm:px-0">
      <h2 className="mb-3 text-lg font-semibold tracking-[-0.01em] text-ink">Fiche de réception</h2>
      <div className="rounded-2xl border border-line bg-surface p-4 shadow-xs sm:p-5">
        <p className="mb-4 text-sm text-ink-3">
          Fiche n° {r.number}
          {r.version > 1 ? `, version ${r.version}` : ''} · établie le {formatDateTime(r.validatedAt, tz)}
          {signed ? ' · signée à l’accueil' : ''}
        </p>
        <EntryReportSummary
          content={r.content}
          receivedByName={r.receivedByName}
          tz={tz}
          photos={(ids) => (
            <p className="text-sm text-ink-3">
              {ids.length} photo{ids.length > 1 ? 's' : ''} dans le PDF de la fiche.
            </p>
          )}
        />
        <div className="mt-5 space-y-3">
          {r.pdfAvailable ? (
            <Button variant="secondary" block className="sm:w-auto" loading={pdf.isPending} onClick={() => pdf.mutate()}>
              Télécharger la fiche (PDF)
            </Button>
          ) : null}
          {pdf.error ? (
            <p role="alert" className="rounded-xl border border-danger-line bg-danger-bg p-3.5 text-danger-fg">
              Le téléchargement n’a pas abouti. Vérifiez votre connexion et réessayez.
            </p>
          ) : null}
          {r.acknowledgment ? (
            <p role="status" className="rounded-xl bg-sunken p-3.5 font-medium text-ink">
              {r.acknowledgment.channel === 'in_person' ? 'Fiche signée au garage' : 'Vous avez pris connaissance de l’état constaté'} le {formatDateTime(r.acknowledgment.at, tz)}.
            </p>
          ) : r.acknowledgeable && !readOnly ? (
            <div className="space-y-2">
              <p className="text-base text-ink-2">
                En confirmant, vous indiquez avoir lu l’état du véhicule constaté à son arrivée et les conditions de gardiennage ci-dessus. Ce n’est pas un accord sur des travaux.
              </p>
              <Button size="lg" block loading={ack.isPending} onClick={() => ack.mutate(r.version)}>
                J’ai pris connaissance de l’état constaté
              </Button>
              {ack.error ? (
                <p role="alert" className="rounded-xl border border-danger-line bg-danger-bg p-3.5 text-danger-fg">
                  {ack.error instanceof ApiError && ack.error.status === 409
                    ? 'La fiche a été mise à jour par le garage. Relisez la nouvelle version.'
                    : 'Votre confirmation n’a pas pu être envoyée. Vérifiez votre connexion et réessayez.'}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
